import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import { IoChevronBack, IoChevronForward, IoFlameOutline, IoLogOut, IoMoon, IoPersonOutline, IoPhonePortraitOutline, IoSchoolOutline, IoSettingsOutline, IoSunny } from 'react-icons/io5'
import { StoreBadges } from './StoreBadges'
import { HebrewTranslateIcon } from '@/components/icons/hebrew-translate-icon'
import { cn } from '@/lib/utils'
import { getAuthEmail, isSignedIn } from '@/lib/baba-auth'
import { isSubscriptionActive } from '@/lib/baba-api'
import { railPopoverLeft, railWidth, readRailExpanded, writeRailExpanded } from '@/lib/railPreference'
import { useSubscriptionStatusQuery } from '../store/babaApi'
import { usePaywall } from './paywall'
import { useSignIn } from './SignInModal'
import { identifyCrispUser } from '@/lib/crisp'
import { useTheme } from '../theme-provider'
import { SignOutConfirm } from './Sidebar'
import { PlanSummary } from './PlanSummary'
import { useEmailGate } from './email/EmailGate'

const TranslateIcon = <HebrewTranslateIcon size={18} />

const ChatIcon = (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
)

const LibraryIcon = (
  // Stack of books — unambiguously a library
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="4" y="4" width="4" height="16" rx="1" />
    <rect x="10" y="4" width="4" height="16" rx="1" />
    <path d="M17 5.5l3 0.7-3 14-3-0.7z" />
  </svg>
)

const TutorIcon = (
  // Microphone: the live voice tutor
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0" />
    <path d="M12 18v3" />
  </svg>
)

const ConversationIcon = (
  // Two people, one interpreter between them
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="7" cy="8" r="3" />
    <circle cx="17" cy="8" r="3" />
    <path d="M2 20a5 5 0 0 1 10 0" />
    <path d="M12 20a5 5 0 0 1 10 0" />
    <path d="M10 4h4" />
  </svg>
)

const SituationsIcon = (
  // A daily message bubble with a check: one Situation a day on WhatsApp
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 12a8 8 0 0 1-11.6 7.1L4 20l1-4.2A8 8 0 1 1 20 12z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
)

const EmailIcon = (
  // An envelope: forwarded Hebrew email, translated
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </svg>
)

const SlangIcon = (
  // Open book with a small speech tail: a dictionary of how people actually talk
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 5.5A2.5 2.5 0 0 1 5.5 3H11v15H5.5A2.5 2.5 0 0 0 3 20.5z" />
    <path d="M21 5.5A2.5 2.5 0 0 0 18.5 3H13v15h5.5a2.5 2.5 0 0 1 2.5 2.5z" />
    <path d="M6.5 7.5h2M6.5 10.5h2M15.5 7.5h2M15.5 10.5h2" />
  </svg>
)

/**
 * The desktop primary rail.
 *
 * Order follows how often a surface is opened: the two daily ones first
 * (Translate, then Chat), then the richer sessions, then Library as the place
 * you go back to. Situations sits dead last — it is a scheduled daily drip on
 * WhatsApp, not something anyone opens mid-task, so it belongs at the tail
 * rather than wedged between surfaces people actually reach for.
 */
type NavItem = { to: string; label: string; icon: React.ReactNode; badge?: string; match: (pathname: string) => boolean }

const NAV: NavItem[] = [
  { to: '/translate', label: 'Translate', icon: TranslateIcon, match: (p) => p.startsWith('/translate') },
  { to: '/chat/x', label: 'Chat', icon: ChatIcon, match: (p) => p.startsWith('/chat') },
  // The speaking game. It sits with the other things you TALK to, not at the
  // tail with Situations — a surface with a daily streak has to be reachable
  // without opening a menu, or the streak is the app's problem and not yours.
  { to: '/learn', label: 'Learn', badge: 'PRE', icon: <IoSchoolOutline size={18} />, match: (p) => p.startsWith('/learn') },
  { to: '/tutor', label: 'Voice Tutor', icon: TutorIcon, match: (p) => p.startsWith('/tutor') },
  { to: '/conversation', label: 'Conversation', icon: ConversationIcon, match: (p) => p.startsWith('/conversation') },
  // Two minutes of speaking: the same place in the list the phone's More
  // sheet gives it. It had a route and a mobile tile but no rail entry.
  { to: '/warm-up', label: 'Warm-up', icon: <IoFlameOutline size={18} />, match: (p) => p.startsWith('/warm-up') },
  { to: '/dictionary', label: 'Slang', icon: SlangIcon, match: (p) => p.startsWith('/dictionary') },
  { to: '/library', label: 'Library', icon: LibraryIcon, match: (p) => p.startsWith('/library') },
  { to: '/situations', label: 'Situations', icon: SituationsIcon, match: (p) => p.startsWith('/situations') },
]

// baba Email sits directly under Library, in the same group, and only for an
// account the server has enabled it for (see EmailGateProvider).
const EMAIL_NAV: NavItem = { to: '/email', label: 'Email', badge: 'New', icon: EmailIcon, match: (p) => p.startsWith('/email') }

function navItems(emailEnabled: boolean): NavItem[] {
  if (!emailEnabled) return NAV
  const libraryIndex = NAV.findIndex((item) => item.to === '/library')
  const at = libraryIndex >= 0 ? libraryIndex + 1 : NAV.length
  return [...NAV.slice(0, at), EMAIL_NAV, ...NAV.slice(at)]
}

const SETTINGS_MATCH = (p: string) => p.startsWith('/settings') || p.startsWith('/account')

/**
 * Collapsed and expanded rows keep the icon on the SAME x — 26px from the
 * rail's left edge either way (8px rail padding + 9px row padding + half of
 * the 18px icon, versus 8px rail padding + half of the 36px square). Without
 * that the icons slide sideways during the width transition, which reads as
 * the whole app shifting rather than a panel opening.
 */
function railItemClasses(active: boolean, expanded: boolean): string {
  return cn(
    'group flex items-center h-9 rounded-md transition-colors duration-150 shrink-0 cursor-pointer select-none active:scale-[0.97]',
    expanded ? 'w-full gap-2.5 px-[9px]' : 'w-9 justify-center',
    active
      ? 'bg-[var(--nav-selected)] text-[var(--accent-deep)] shadow-2xs font-semibold ring-1 ring-inset ring-[var(--nav-selected-border)]'
      : 'text-[var(--muted-2)] hover:bg-[var(--card-2)] hover:text-[var(--ink)]',
  )
}

/**
 * A panel anchored beside the rail, rendered into <body> rather than inside
 * the rail.
 *
 * The rail clips to its own width — overflow-hidden is what stops labels
 * spilling while it animates between 52px and 200px — so a panel positioned
 * in the gutter beside it laid out correctly and was then clipped away
 * entirely: invisible, and skipped by hit-testing, so hovering and clicking
 * did nothing. A portal escapes that clip. The rail is flush against the
 * viewport's left edge, so its own left offset is already the viewport x.
 */
function RailPopover({
  anchorRef,
  expanded,
  onDismiss,
  role,
  label,
  width,
  className,
  children,
}: {
  anchorRef: React.RefObject<HTMLElement | null>
  expanded: boolean
  onDismiss: () => void
  role: 'dialog' | 'menu'
  label?: string
  width: number
  className?: string
  children: React.ReactNode
}) {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [bottom, setBottom] = useState(0)

  // Bottom-aligned to its trigger, measured before paint so it never flashes
  // at the wrong height, and re-measured when the rail resizes under it.
  useLayoutEffect(() => {
    const place = () => {
      const anchor = anchorRef.current
      if (anchor) setBottom(window.innerHeight - anchor.getBoundingClientRect().bottom)
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [anchorRef, expanded])

  // Outside-click has to treat the portalled panel as "inside" or the panel
  // would unmount on mousedown and eat the click aimed at a store badge.
  useEffect(() => {
    const onPointer = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null
      if (!target) return
      if (panelRef.current?.contains(target) || anchorRef.current?.contains(target)) return
      onDismiss()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss()
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('touchstart', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('touchstart', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [anchorRef, onDismiss])

  return createPortal(
    <div
      ref={panelRef}
      role={role}
      aria-label={label}
      className={cn('fixed z-[120] rounded-md animate-scale-in', className)}
      style={{
        left: railPopoverLeft(expanded),
        bottom,
        width,
        background: 'var(--card)',
        border: '1px solid var(--line)',
        boxShadow: 'var(--shadow-lg)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      {children}
    </div>,
    document.body,
  )
}

/**
 * One line, always. The address used to wrap at the "@" so a narrow rail read
 * "name@" / "gmail.com" rather than a mid-token cut — correct in isolation, but
 * a two-line row in a column of single-line rows reads as a layout bug, and the
 * rail is an identity chip rather than a place anyone reads an address off.
 * The full address stays in the row's title attribute and in the account menu.
 */
function EmailText({ email, className, style }: { email: string; className?: string; style?: React.CSSProperties }) {
  return (
    <span className={cn('block truncate whitespace-nowrap', className)} style={style} title={email}>
      {email}
    </span>
  )
}

function RailLabel({ children, active }: { children: React.ReactNode; active?: boolean }) {
  return (
    <span
      className="animate-fade-in min-w-0 truncate text-[13px] font-medium"
      style={{ color: active ? 'var(--accent-deep)' : 'var(--ink)' }}
    >
      {children}
    </span>
  )
}

/** Desktop primary navigation — a 52px icon rail that pops out to 200px. */
export default function Rail() {
  const { pathname } = useLocation()
  const { open: openPaywall } = usePaywall()
  const { open: openSignIn } = useSignIn()
  const { resolvedTheme, toggle } = useTheme()
  // Read in the initializer: an effect left the first frame showing "Sign In
  // Free" to someone who is signed in, then swapped the account row in.
  const [email, setEmail] = useState<string | null>(() => getAuthEmail())
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false)
  const [accountMenuOpen, setAccountMenuOpen] = useState(false)
  const [appMenuOpen, setAppMenuOpen] = useState(false)
  // Read straight from storage in the initializer so the rail paints at its
  // remembered width on the first frame — a mount-effect read would flash the
  // collapsed rail and then shove the whole app 148px right.
  const [expanded, setExpanded] = useState<boolean>(() =>
    readRailExpanded(typeof localStorage === 'undefined' ? null : localStorage),
  )
  const appMenuRef = useRef<HTMLDivElement | null>(null)
  const accountMenuRef = useRef<HTMLDivElement | null>(null)

  const signedIn = isSignedIn()
  const { enabled: emailEnabled } = useEmailGate()
  const { data: subscription } = useSubscriptionStatusQuery(undefined, { skip: !signedIn })
  const isPro = subscription ? isSubscriptionActive(subscription) : false

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev
      writeRailExpanded(typeof localStorage === 'undefined' ? null : localStorage, next)
      return next
    })
  }, [])

  useEffect(() => {
    if (!signedIn || !email) return
    identifyCrispUser({ email, plan: subscription?.plan ?? null, isPro })
  }, [signedIn, email, subscription?.plan, isPro])

  useEffect(() => {
    setEmail(getAuthEmail())
  }, [pathname])

  useEffect(() => {
    setAccountMenuOpen(false)
    setAppMenuOpen(false)
  }, [pathname])

  // Dismissal lives in RailPopover, which can see the portalled panel; these
  // just have to be stable so it does not resubscribe on every render.
  const closeAppMenu = useCallback(() => setAppMenuOpen(false), [])
  const closeAccountMenu = useCallback(() => setAccountMenuOpen(false), [])

  const initial = (email ? email.trim()[0] : '')?.toUpperCase() || ''

  const settingsActive = SETTINGS_MATCH(pathname)
  // Collapsed rows have no visible name, so the native tooltip is the only
  // way to read one. Expanded rows already show it — a tooltip repeating the
  // label under the cursor is noise.
  const tip = (label: string) => (expanded ? undefined : label)

  return (
    <nav
      id="sidebar-nav"
      aria-label="Main Navigation"
      data-expanded={expanded ? 'true' : 'false'}
      className={cn(
        'relative hidden md:flex flex-col shrink-0 py-3 px-2 gap-1.5 bg-[var(--cream-2)] border-r border-app overflow-hidden',
        expanded ? 'items-stretch' : 'items-center',
      )}
      style={{ width: railWidth(expanded), transition: 'width var(--duration-base, 220ms) var(--ease-out, ease)' }}
    >
      <Link
        to="/"
        aria-label="baba home"
        title={tip('baba Home')}
        className={cn('mb-2.5 flex items-center h-7 shrink-0', expanded ? 'gap-2.5 px-[9px]' : 'justify-center')}
      >
        <img src="/baba-app-icon.png" alt="" className="w-7 h-7 rounded-lg shrink-0" style={expanded ? { marginLeft: -5 } : undefined} />
        {expanded && (
          <span className="animate-fade-in text-[15px] font-semibold tracking-tight" style={{ color: 'var(--ink)' }}>
            baba
          </span>
        )}
      </Link>

      {navItems(emailEnabled).map((item) => {
        const active = item.match(pathname)
        return (
          <Link
            key={item.to}
            to={item.to}
            title={tip(item.label)}
            aria-label={item.badge === 'PRE' ? `${item.label} (Preview)` : item.badge ? `${item.label} (${item.badge})` : item.label}
            aria-current={active ? 'page' : undefined}
            className={cn('relative', railItemClasses(active, expanded))}
          >
            <span className="shrink-0 inline-flex">{item.icon}</span>
            {expanded && <RailLabel active={active}>{item.label}</RailLabel>}
            {/* Collapsed, the badge is a dot: a two-letter pill under a 18px
                icon is unreadable, and an unreadable label is just noise. */}
            {item.badge && (expanded ? (
              <span
                className="ml-auto shrink-0 rounded-full px-1.5 py-0.5"
                style={{ background: 'var(--accent-soft)', color: 'var(--accent-ink)', fontSize: 9, fontWeight: 700, letterSpacing: '0.08em' }}
              >
                {item.badge}
              </span>
            ) : (
              <span
                className="absolute top-1 right-1 rounded-full"
                style={{ width: 6, height: 6, background: 'var(--accent-deep)' }}
                aria-hidden="true"
              />
            ))}
          </Link>
        )
      })}

      <div className="flex-1" />

      {/* The mobile app is always one click away, for every audience. */}
      <div ref={appMenuRef} className={cn('relative shrink-0', expanded && 'w-full')}>
        <button
          type="button"
          onClick={() => setAppMenuOpen((v) => !v)}
          aria-haspopup="dialog"
          aria-expanded={appMenuOpen}
          title={tip('Get the Mobile App')}
          aria-label="Get the Mobile App"
          className={railItemClasses(appMenuOpen, expanded)}
        >
          <span className="shrink-0 inline-flex"><IoPhonePortraitOutline size={18} /></span>
          {expanded && <RailLabel active={appMenuOpen}>Get the App</RailLabel>}
        </button>
        {appMenuOpen && (
          <RailPopover
            anchorRef={appMenuRef}
            expanded={expanded}
            onDismiss={closeAppMenu}
            role="dialog"
            label="Get the Mobile App"
            width={280}
            className="p-3.5 flex flex-col gap-3"
          >
            <div>
              <div className="text-[13.5px] font-semibold" style={{ color: 'var(--ink)' }}>Get the Mobile App</div>
              <div className="text-[12px] mt-0.5" style={{ color: 'var(--muted)' }}>
                Camera, voice, and offline phrases. Your plan carries over. Sign in with the same email.
              </div>
            </div>
            <div className="flex flex-col gap-2" dir="ltr">
              <StoreBadges placement="rail" />
            </div>
          </RailPopover>
        )}
      </div>

      <button
        type="button"
        onClick={toggle}
        title={tip(`Switch to ${resolvedTheme === 'dark' ? 'Light' : 'Dark'} Mode`)}
        aria-label={`Switch to ${resolvedTheme === 'dark' ? 'Light' : 'Dark'} Mode`}
        className={railItemClasses(false, expanded)}
      >
        <span className="shrink-0 inline-flex">
          {resolvedTheme === 'dark' ? <IoSunny size={17} /> : <IoMoon size={17} />}
        </span>
        {expanded && <RailLabel>{resolvedTheme === 'dark' ? 'Light Mode' : 'Dark Mode'}</RailLabel>}
      </button>

      <Link
        to="/settings"
        title={tip('Settings')}
        aria-label="Settings"
        aria-current={settingsActive ? 'page' : undefined}
        className={railItemClasses(settingsActive, expanded)}
      >
        <span className="shrink-0 inline-flex"><IoSettingsOutline size={18} /></span>
        {expanded && <RailLabel active={settingsActive}>Settings</RailLabel>}
      </Link>

      {expanded && <div className="mt-1 w-full"><PlanSummary variant="rail" /></div>}

      {email ? (
        <div ref={accountMenuRef} className={cn('relative mt-1 shrink-0', expanded && 'w-full')}>
          <button
            type="button"
            onClick={() => setAccountMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={accountMenuOpen}
            aria-label="Account Menu"
            title={tip(email)}
            className={cn(
              'group flex items-center transition-colors duration-150 rounded-md active:scale-[0.98] cursor-pointer',
              expanded ? 'w-full gap-2.5 min-h-9 py-1.5 px-[9px] hover:bg-[var(--card-2)]' : 'justify-center h-9 w-9 hover:bg-[var(--card-2)]',
            )}
          >
            <span
              className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-[12px] font-semibold"
              style={{ background: 'var(--accent-soft)', color: 'var(--accent-ink)', marginLeft: expanded ? -5 : 0 }}
            >
              {initial}
            </span>
            {expanded && (
              <span
                className="animate-fade-in min-w-0 flex-1 text-left text-[12.5px] leading-[1.25] truncate"
                style={{ color: 'var(--muted)' }}
              >
                Account
              </span>
            )}
          </button>

          {accountMenuOpen && (
            <RailPopover
              anchorRef={accountMenuRef}
              expanded={expanded}
              onDismiss={closeAccountMenu}
              role="menu"
              label="Account"
              width={240}
              className="py-1.5"
            >
              <div className="px-3.5 py-2.5 border-b border-app">
                <EmailText email={email} className="block text-[13px] font-semibold text-app" />
                <PlanSummary variant="menu" />
              </div>

              <Link
                to="/settings/plan"
                role="menuitem"
                onClick={() => setAccountMenuOpen(false)}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[14px] text-app hover:bg-[var(--card-2)] active:scale-[0.99] transition-colors cursor-pointer font-medium"
              >
                Plan & Usage
              </Link>

              <Link
                to="/profile"
                role="menuitem"
                onClick={() => setAccountMenuOpen(false)}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[14px] text-app hover:bg-[var(--card-2)] active:scale-[0.99] transition-colors cursor-pointer font-medium"
              >
                <IoPersonOutline size={15} className="text-subtle" />
                Your Profile
              </Link>

              <Link
                to="/settings"
                role="menuitem"
                onClick={() => setAccountMenuOpen(false)}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[14px] text-app hover:bg-[var(--card-2)] active:scale-[0.99] transition-colors cursor-pointer font-medium"
              >
                <IoSettingsOutline size={15} className="text-subtle" />
                Settings
              </Link>

              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAccountMenuOpen(false)
                  setShowSignOutConfirm(true)
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[14px] text-app hover:bg-red-50/60 dark:hover:bg-red-950/25 hover:text-red-600 dark:hover:text-red-400 active:scale-[0.99] transition-colors cursor-pointer font-medium"
              >
                <IoLogOut size={15} className="text-subtle" />
                Log Out
              </button>
            </RailPopover>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => openSignIn('sidebar')}
          aria-label="Sign In"
          title={tip('Sign In')}
          className={cn(
            'group flex items-center mt-1 shrink-0 rounded-md transition-colors duration-150 active:scale-[0.98] cursor-pointer',
            expanded ? 'w-full gap-2.5 h-9 px-[9px] hover:bg-[var(--card-2)]' : 'justify-center h-9 w-9 hover:bg-[var(--card-2)]',
          )}
        >
          <span
            className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full"
            style={{ background: 'var(--accent-soft)', color: 'var(--accent-ink)', marginLeft: expanded ? -5 : 0 }}
          >
            <IoPersonOutline size={15} />
          </span>
          {expanded && (
            <span className="animate-fade-in truncate text-[13px] font-semibold" style={{ color: 'var(--accent-deep)' }}>
              Sign In Free
            </span>
          )}
        </button>
      )}

      {/* Collapse used to be a full row with its own label and divider, which
        * spent a whole line of the rail on a control people use once. It is a
        * 32px button at the end of the account row now, and keeps its own row
        * only when the rail is collapsed and there is nothing beside it. */}
      <button
        type="button"
        onClick={toggleExpanded}
        aria-expanded={expanded}
        aria-controls="sidebar-nav"
        title={expanded ? 'Collapse Sidebar' : 'Expand Sidebar'}
        aria-label={expanded ? 'Collapse Sidebar' : 'Expand Sidebar'}
        className={cn(
          'group flex items-center justify-center shrink-0 rounded-md transition-colors duration-150 cursor-pointer text-[var(--muted-2)] hover:bg-[var(--card-2)] hover:text-[var(--ink)]',
          expanded ? 'absolute bottom-3 right-2 h-8 w-8' : 'mt-1 h-9 w-9',
        )}
      >
        <span className="shrink-0 inline-flex">
          {expanded ? <IoChevronBack size={17} /> : <IoChevronForward size={17} />}
        </span>
      </button>

      {showSignOutConfirm && <SignOutConfirm onCancel={() => setShowSignOutConfirm(false)} />}

      {/* Hidden paywall trigger kept for feature parity; not visible */}
      <button type="button" onClick={() => openPaywall('upgrade')} className="hidden" aria-hidden />
    </nav>
  )
}
